import { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { CTD_SECTION_LIST } from '@/lib/ctdSections';

interface Props {
  onAssign: (section: string) => void;
  onDismiss: () => void;
  disabled?: boolean;
}

export function SectionPicker({ onAssign, onDismiss, disabled }: Props) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const lowerQuery = query.toLowerCase();
  const filtered = query
    ? CTD_SECTION_LIST.filter(
        (s) =>
          s.section.toLowerCase().includes(lowerQuery) ||
          s.title.toLowerCase().includes(lowerQuery)
      )
    : [];

  const showDropdown = focused && query.length > 0 && filtered.length > 0;

  const handleSelect = (section: string) => {
    setQuery(section);
    setSelectedSection(section);
    setFocused(false);
  };

  const handleAssign = () => {
    const section = selectedSection || query.trim().toUpperCase();
    if (section) onAssign(section);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      // If there's exactly one match, select it
      if (filtered.length === 1) {
        handleSelect(filtered[0].section);
      } else if (selectedSection || query.trim()) {
        handleAssign();
      }
    }
  };

  return (
    <div ref={containerRef} className="flex items-center gap-2 mt-2.5">
      {/* Section Input */}
      <div className="relative flex-1 max-w-[240px]">
        <div className="flex items-center border border-slate-200 rounded-lg bg-white overflow-hidden focus-within:border-brand-orange focus-within:ring-2 focus-within:ring-brand-orange/20">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedSection(null); setFocused(true); }}
            onFocus={() => setFocused(true)}
            onKeyDown={handleKeyDown}
            placeholder="Section # (e.g. 5.3.5.1)"
            disabled={disabled}
            className="flex-1 text-sm px-3 py-1.5 focus:outline-none disabled:bg-slate-50 min-w-0"
          />
          <Search className="w-3.5 h-3.5 text-slate-400 mr-2.5 shrink-0" />
        </div>

        {/* Suggestions Dropdown */}
        {showDropdown && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {filtered.slice(0, 8).map((s) => (
              <button
                key={s.section}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(s.section)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 transition-colors border-b border-slate-50 last:border-0"
              >
                <span className="font-medium text-brand-charcoal">{s.section}</span>
                <span className="text-slate-500 ml-2 truncate">{s.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Assign */}
      <button
        onClick={handleAssign}
        disabled={disabled || (!selectedSection && !query.trim())}
        className="px-3 py-1.5 bg-brand-orange text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
      >
        Assign
      </button>

      {/* Dismiss */}
      <button
        onClick={onDismiss}
        disabled={disabled}
        className="px-3 py-1.5 text-slate-500 text-xs font-medium rounded-lg hover:bg-slate-50 hover:text-slate-700 transition-colors disabled:opacity-40 whitespace-nowrap"
      >
        Dismiss
      </button>
    </div>
  );
}
